"use client";

import Image from "next/image";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Icon } from "../_family/Icon";
import { useReducedMotion } from "../_family/useReducedMotion";
import type { GrownLine, ShippedFile } from "./content";
import { Tokens } from "./Tokens";
import { THE_ASK, TRAVELLER } from "./colour";
import { STEPS } from "./words";

const REPLY = "On it. Looking for the next flight to Mumbai.";

/** A step stopped part way (another step was picked). */
class Stopped extends Error {}

function Face({ className }: { className?: string }) {
  return (
    <span className={className ? `face ${className}` : "face"}>
      <Image
        src="/developers/family/face-bash.webp"
        width={320}
        height={320}
        alt=""
        unoptimized
      />
    </span>
  );
}

/**
 * The code that grows: one file, a line or two per step, the new lines in the step's colour, and under it what
 * Bash does with them. The steps are tabs (a list beside the code, pills on a phone); the yes card answers.
 */
export function CodeGrows({
  code,
  file,
  ship,
}: {
  code: GrownLine[];
  file: ShippedFile;
  ship: Array<{ text: string; isComment: boolean }>;
}) {
  const [step, setStep] = useState(0);
  const [run, setRun] = useState(0);
  const [reply, setReply] = useState("");
  const [answered, setAnswered] = useState<"yes" | "no" | null>(null);
  const [traced, setTraced] = useState(1);
  const [passed, setPassed] = useState(0);
  const [shell, setShell] = useState(0);
  const [done, setDone] = useState(false);

  const epoch = useRef(0);
  const still = useReducedMotion();
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const pills = useRef<HTMLDivElement>(null);
  const stepButtons = useRef<Array<HTMLButtonElement | null>>([]);
  const pillButtons = useRef<Array<HTMLButtonElement | null>>([]);

  const k = step + 1;
  const s = STEPS[step];
  const onLines = code.filter((l) => l.from <= k && (!l.to || k <= l.to));

  // Each step plays what Bash does with it, from the start, whenever it is picked.
  useEffect(() => {
    const e = ++epoch.current;
    const wait = (ms: number) =>
      new Promise<void>((resolve, reject) =>
        setTimeout(
          () => (e === epoch.current ? resolve() : reject(new Stopped())),
          still ? 0 : ms
        )
      );
    (async () => {
      if (k === 1) {
        const words = REPLY.split(" ");
        await wait(500);
        for (let i = 1; i <= words.length; i++) {
          setReply(words.slice(0, i).join(" "));
          await wait(80);
        }
      }
      if (k === 3) {
        await wait(700);
        setTraced(2);
        await wait(900);
        setTraced(3);
      }
      if (k === 4) {
        // The shell's lines arrive around the run: the test command first, the deploy once it passes.
        const half = Math.ceil(ship.length / 2);
        for (let i = 1; i <= half; i++) {
          await wait(i === 1 ? 300 : 400);
          setShell(i);
        }
        for (let i = 1; i <= file.tests.length; i++) {
          await wait(380);
          setPassed(i);
        }
        setDone(true);
        for (let i = half + 1; i <= ship.length; i++) {
          await wait(i === half + 1 ? 300 : 600);
          setShell(i);
        }
      }
    })().catch((err) => {
      if (!(err instanceof Stopped)) throw err;
    });
  }, [k, run, still, file.tests.length, ship.length]);

  /** A step begins again from its start: picked, or the section first seen. */
  const begin = useCallback((n: number) => {
    setStep(n);
    setRun((r) => r + 1);
    setReply("");
    setAnswered(null);
    setTraced(1);
    setPassed(0);
    setShell(0);
    setDone(false);
  }, []);

  // Seen for the first time, it starts again from the first step.
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        begin(0);
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    const current = epoch;
    return () => {
      io.disconnect();
      current.current++;
    };
  }, [begin]);

  // On a phone the pills scroll so the picked one is in view.
  useEffect(() => {
    const row = pills.current;
    const pill = pillButtons.current[step];
    if (!row || !pill || row.scrollWidth <= row.clientWidth) return;
    row.scrollTo({
      left: pill.offsetLeft - (row.clientWidth - pill.offsetWidth) / 2,
      behavior: still ? "auto" : "smooth",
    });
  }, [step, still]);

  const pick = (n: number, focus = false) => {
    begin(n);
    if (!focus) return;
    const listShown =
      list.current && getComputedStyle(list.current).display !== "none";
    (listShown ? stepButtons : pillButtons).current[n]?.focus();
  };
  const arrows = (vertical: boolean) => (ev: KeyboardEvent) => {
    const fwd = vertical ? "ArrowDown" : "ArrowRight";
    const back = vertical ? "ArrowUp" : "ArrowLeft";
    if (ev.key !== fwd && ev.key !== back) return;
    ev.preventDefault();
    pick((step + (ev.key === fwd ? 1 : STEPS.length - 1)) % STEPS.length, true);
  };
  const answer = async (choice: "yes" | "no") => {
    const e = epoch.current;
    await new Promise((r) => setTimeout(r, still ? 0 : 500));
    if (e === epoch.current) setAnswered(choice);
  };

  return (
    <div className="path">
      <ol
        className="steps"
        ref={list}
        role="tablist"
        aria-orientation="vertical"
        aria-label="Steps"
      >
        {STEPS.map((st, i) => (
          <li
            key={st.title}
            className={i === step ? "on" : undefined}
            role="presentation"
          >
            <button
              type="button"
              role="tab"
              id={`vf-step-${i + 1}`}
              aria-selected={i === step}
              aria-controls="vf-path-panel"
              tabIndex={i === step ? 0 : -1}
              ref={(el) => {
                stepButtons.current[i] = el;
              }}
              onClick={() => pick(i)}
              onKeyDown={arrows(true)}
            >
              <b>{st.title}</b>
              <span className="sign" aria-hidden="true">
                <i>+</i>
              </span>
              <span className="say">
                <span>
                  <span>{st.says}</span>
                </span>
              </span>
            </button>
          </li>
        ))}
      </ol>
      <div className="path-main">
        <div
          className="step-pills"
          ref={pills}
          role="tablist"
          aria-label="Steps"
        >
          {STEPS.map((st, i) => (
            <button
              key={st.pill}
              type="button"
              role="tab"
              aria-selected={i === step}
              aria-controls="vf-path-panel"
              tabIndex={i === step ? 0 : -1}
              ref={(el) => {
                pillButtons.current[i] = el;
              }}
              onClick={() => pick(i)}
              onKeyDown={arrows(false)}
            >
              {st.pill}
            </button>
          ))}
        </div>
        <p className="step-desc">{s.says}</p>
        <div
          className="pstage wash rv"
          id="vf-path-panel"
          ref={panel}
          role="tabpanel"
          aria-labelledby={`vf-step-${k}`}
          data-c={s.colour}
        >
          <div className="edwin">
            <div className="edwin-bar">
              <span className="lights">
                <i />
                <i />
                <i />
              </span>
              <div className="ftabs">
                <span className={s.file === "ts" ? "on" : undefined}>
                  <Icon name="fileTs" />
                  server.ts
                </span>
                <span className={s.file === "yaml" ? "on" : undefined}>
                  <Icon name="fileYaml" />
                  vestra.yaml
                </span>
              </div>
              <small>
                {s.file === "ts"
                  ? `${onLines.length} lines`
                  : `tests: 1 of ${file.tests.length} shown`}
              </small>
            </div>
            <div
              className={
                s.file === "ts" ? "code code-file on" : "code code-file"
              }
            >
              <div className="code-in">
                {code.map((l, i) => {
                  const on = l.from <= k && (!l.to || k <= l.to);
                  const fresh = on && l.from === k && k > 1;
                  return (
                    <div
                      key={i}
                      className={`l${on ? " on" : ""}${fresh ? " new" : ""}`}
                      aria-hidden={on ? undefined : true}
                    >
                      <span>
                        {l.tokens.length ? <Tokens tokens={l.tokens} /> : " "}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              className={
                s.file === "yaml" ? "code code-file on" : "code code-file"
              }
            >
              <div className="code-in">
                {file.lines.map((l, i) => (
                  <div
                    key={i}
                    className={`l on${l.isNew && k === 4 ? " new" : ""}`}
                  >
                    <span>
                      {l.tokens.length ? <Tokens tokens={l.tokens} /> : " "}
                    </span>
                  </div>
                ))}
              </div>
              <div className="shell">
                {ship.map((l, i) => (
                  <p key={i} className={i < shell ? "on" : undefined}>
                    {l.isComment ? (
                      <span className="t-c">{l.text}</span>
                    ) : (
                      <>
                        <span className="pr">$</span> {l.text}
                      </>
                    )}
                  </p>
                ))}
              </div>
            </div>
          </div>

          <div className="whs">
            <div className={k === 1 ? "wh on" : "wh"}>
              <div className="card-w">
                <div className="wh-head">
                  <Face />
                  <div>
                    <b>Bash, for {TRAVELLER}</b>
                    <small>Their own Bash, made just now</small>
                  </div>
                  <span className="tag">test mode</span>
                </div>
                <div className="wh-chat">
                  <span className="said-me me">{THE_ASK}</span>
                  <div className="wh-reply">
                    <Face />
                    <p>{reply}</p>
                  </div>
                </div>
                <div className="wh-row first">
                  <code>task.started</code>
                  <span>Rebook the flight</span>
                </div>
              </div>
            </div>

            <div className={k === 2 ? "wh on" : "wh"}>
              <div className={answered ? "card-w done" : "card-w"}>
                <div className="wh-head">
                  <Face />
                  <div>
                    <b>Bash needs {TRAVELLER}&apos;s okay</b>
                    <small>Shown in your app, answered by them</small>
                  </div>
                  <span className="tag booking">Booking</span>
                </div>
                <p className="what">Book the 09:15 to Mumbai, seat 14C?</p>
                <p className="why">
                  The fare difference is ₹1,200. Nothing is booked until they
                  say yes.
                </p>
                <div className="acts">
                  <button
                    className="btn btn-soft"
                    type="button"
                    onClick={() => void answer("no")}
                  >
                    Not now
                  </button>
                  <button
                    className="btn btn-ink"
                    type="button"
                    onClick={() => void answer("yes")}
                  >
                    Yes, book it
                  </button>
                </div>
                <div className={answered === "no" ? "result no" : "result"}>
                  <span className="check">
                    <Icon name={answered === "no" ? "replay" : "check"} />
                  </span>
                  <div>
                    <b>
                      {answered === "no"
                        ? `Not booked. ${TRAVELLER} said not now.`
                        : `Booked. ${TRAVELLER} said yes.`}
                    </b>
                    <span>
                      interaction.resolved, approve:{" "}
                      {answered === "no" ? "false" : "true"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={k === 3 ? "wh on" : "wh"}>
              <div className="card-w">
                <div className="trace">
                  <div className="trace-row">
                    <span className="dot">
                      <Face />
                    </span>
                    <div>
                      <b>Bash called get_booking</b>
                      <pre>{"{ ref: 'TN-48213' }"}</pre>
                    </div>
                  </div>
                  <div
                    className={
                      traced >= 2 ? "trace-row wait in" : "trace-row wait"
                    }
                  >
                    <span className="dot you">
                      <Icon name="function" />
                    </span>
                    <div>
                      <b>Your function answered, on your server</b>
                      <pre>
                        {
                          "{ from: 'DEL', to: 'BOM', leaves: '06:10', status: 'cancelled' }"
                        }
                      </pre>
                    </div>
                  </div>
                  <div
                    className={
                      traced >= 3 ? "trace-row wait in" : "trace-row wait"
                    }
                  >
                    <span className="dot">
                      <Face />
                    </span>
                    <div>
                      <b>Bash carried on</b>
                      <pre>
                        The next flight to Mumbai is the 09:15. Shall I book it?
                      </pre>
                    </div>
                  </div>
                </div>
                <p className="trace-foot">
                  Bash sees what your function returns, and nothing else from
                  your systems.
                </p>
              </div>
            </div>

            <div className={k === 4 ? "wh on" : "wh"}>
              <div className="card-w">
                <div className="wh-head">
                  <span className="face tests-face">
                    <Icon name="flask" />
                  </span>
                  <div>
                    <b>{file.name}, version 3</b>
                    <small>
                      Tested in test mode, judged against each expect
                    </small>
                  </div>
                </div>
                <ul className="tests">
                  {file.tests.map((t, i) => (
                    <li key={t} className={i < passed ? "ok" : undefined}>
                      <i>
                        <Icon name="check" />
                      </i>
                      {t}
                    </li>
                  ))}
                </ul>
                <div className="tests-foot">
                  <b className={done ? undefined : "pend"}>
                    {done
                      ? `${file.tests.length} of ${file.tests.length} passed. Promoted in test mode.`
                      : "Testing…"}
                  </b>
                  <code>vestra deploy --live</code>
                </div>
              </div>
            </div>
          </div>

          <div className="path-next">
            <button
              className="btn btn-soft"
              type="button"
              onClick={() => pick((step + 1) % STEPS.length)}
            >
              <span>{s.next}</span>
              <Icon name="arrowRight" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
